import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  Modal,
  PageHeader,
  Row,
  Select,
  Spin,
  Statistic,
  Switch,
  Tabs,
  Tag,
  Tooltip,
  Upload,
  message,
} from "antd";
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  FileTextOutlined,
  FormOutlined,
  InboxOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

import { templatesApi } from "../../api/presentationsApi";
import PdfPreviewModal from "../../components/business/presentations/PdfPreviewModal";
import TemplateEditModal from "../../components/business/presentations/TemplateEditModal";
import TemplateScopeFields from "../../components/business/presentations/TemplateScopeFields";
import TemplateDrawer from "../../components/business/presentations/TemplateDrawer";
import PresentationMonitor from "../../components/business/presentations/PresentationMonitor";
import DriveFolders from "../../components/business/presentations/DriveFolders";
import DeclareCopyFieldsModal from "../../components/business/copy/DeclareCopyFieldsModal";
import {
  ICheckResult,
  IPresentationTemplate,
  NAMESPACE_LABELS,
  TemplateScope,
} from "../../types/models/presentations";

/**
 * Templates — a gallery, not a table.
 *
 * A template is a design; people recognise designs by looking at them, and a row
 * reading `presentations/templates/summer_promo_ES.pptx` communicates nothing to
 * someone who has been working in Canva.
 *
 * The upload flow validates before it stores. The engine hard-fails on a token it
 * cannot fill, and without a check here that surfaces days later as a raw list of
 * strings on somebody else's brochure. Catching it at upload turns the worst
 * failure mode into something closer to a spellcheck.
 */

const Container = styled.div`
  height: calc(100% + 39px);
  overflow-y: auto;

  /* The gallery.

     Plain CSS grid rather than antd's Row/Col, because the card width is the
     thing being controlled and a 24-column system expresses it as a fraction
     of whatever the viewport happens to be. lg={4} meant "six per row" — at
     1600px that is a 250px card, at 2560px a 420px one — and the maxWidth
     that used to sit on the card only capped its content, leaving uneven gaps
     inside columns that were still a sixth of the screen each.

     auto-fill rather than auto-fit: auto-fit collapses the empty tracks and
     lets a lone card on the last row stretch the full width, which is the
     same unpredictability in a different place. Leftover space stays empty
     and the cards stay the size they are.

     The min(350px, 100%) floor keeps a viewport narrower than one card from
     overflowing sideways — below that the track shrinks with the screen
     instead of pushing out a horizontal scrollbar. */
  .gallery {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(275px, 100%), 300px));
    gap: 16px;
    padding: 24px 0;
  }

  .scope-tag {
    font-size: 11px;
  }

  .tpl-meta {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
  }

  .tpl-count {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #f0f0f0;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.65);
  }

  .thumb {
    height: 190px;
    background: linear-gradient(160deg, #f0f2f5 0%, #e6e9ee 100%);
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(0, 0, 0, 0.25);
    font-size: 34px;
    overflow: hidden;
  }

  .thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top center;
    display: block;
  }
`;

const SCOPE_COUNT_LABELS: Record<TemplateScope, (n: number) => string> = {
  // A template can name several properties, so the label counts them rather
  // than claiming there is one.
  listing: (n) => (n === 1 ? "1 property" : `${n} properties`),
  destination: () => "Destination",
  default: () => "Default — all properties",
};

const SCOPE_COLORS: Record<TemplateScope, string> = {
  listing: "blue",
  destination: "geekblue",
  default: "default",
};

/**
 * `children` is shown where the verdict goes. The upload modal uses it for the
 * copy-list prompt: an undescribed copy list is a step still to take in this
 * modal, not a defect in the file, so it is listed there once and not repeated
 * here as an error.
 */
function ValidationReport({
  result,
  children,
}: {
  result: ICheckResult;
  children?: React.ReactNode;
}) {
  const { manifest, validation } = result;
  const blocking = validation.problems.filter(
    (problem) => problem.kind !== "undeclared_copy"
  );
  return (
    <>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Statistic title="Slides" value={manifest.slides} />
        </Col>
        <Col span={6}>
          <Statistic
            title="Text tokens"
            value={manifest.tokens.length}
            suffix={
              validation.resolvable_tokens < manifest.tokens.length
                ? `(${validation.resolvable_tokens} ok)`
                : undefined
            }
          />
        </Col>
        <Col span={6}>
          <Statistic title="Image slots" value={manifest.image_slots.length} />
        </Col>
        <Col span={6}>
          <Statistic title="Canvas" value={manifest.canvas_slots.length} />
        </Col>
      </Row>

      {validation.valid ? (
        <Alert
          type="success"
          showIcon
          icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}
          message="Every token in this template can be filled"
        />
      ) : blocking.length > 0 && (
        <Alert
          type="error"
          showIcon
          icon={<CloseCircleTwoTone twoToneColor="#ff4d4f" />}
          message="This template would fail every time it ran"
          description={
            <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
              {blocking.map((problem) => (
                <li key={problem.name} style={{ marginBottom: 6 }}>
                  <code>
                    {problem.kind === "unregistered_canvas"
                      ? problem.name
                      : `{{${problem.name}}}`}
                  </code>
                  {problem.slides.length > 0 && (
                    <> on slide {problem.slides.join(", ")}</>
                  )}
                  {problem.kind === "unknown_token"
                    ? " isn’t a valid token."
                    : " has no renderer registered."}
                  {problem.suggestion && (
                    <div style={{ color: "rgba(0,0,0,.55)" }}>
                      Did you mean <code>{problem.suggestion}</code>? Fix it in
                      PowerPoint and upload again.
                    </div>
                  )}
                </li>
              ))}
            </ul>
          }
        />
      )}

      {children}

      {(manifest.hidden_slides?.length ?? 0) > 0 && (
        <Alert
          type="info"
          showIcon
          icon={<EyeInvisibleOutlined />}
          style={{ marginTop: 12 }}
          message={`${manifest.hidden_slides!.length} of these slides are hidden in PowerPoint`}
          description={`Slide ${manifest.hidden_slides!.join(", ")} will be filled but left out of the PDF, so the PDF will have ${manifest.visible_slides} pages. Un-hide them in PowerPoint if that is not what you want.`}
        />
      )}

      {manifest.image_slots.length > 0 && (
        <p className="tpl-meta" style={{ marginTop: 12 }}>
          Image slots: {manifest.image_slots.join(", ")}
        </p>
      )}
    </>
  );
}

function Templates() {
  const [templates, setTemplates] = useState<IPresentationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  // Retired designs pile up and get picked by mistake, so the gallery shows
  // only what can generate unless someone asks for the rest.
  const [showInactive, setShowInactive] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [checking, setChecking] = useState(false);
  const [checked, setChecked] = useState<ICheckResult | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadScope, setUploadScope] = useState<TemplateScope>("default");
  const [editing, setEditing] = useState<IPresentationTemplate | null>(null);
  const [opened, setOpened] = useState<IPresentationTemplate | null>(null);
  /** Set when the monitor opens the drawer at one property's row. */
  const [drawerQuery, setDrawerQuery] = useState<string>();
  /**
   * One viewer serves three callers: a template preview, which has to be built
   * first, and any finished brochure, whose PDF already exists.
   */
  const [viewing, setViewing] = useState<{
    title: string;
    url: string | null;
    loading: boolean;
    hint?: string;
    error?: string | null;
    downloadUrl?: string | null;
    downloadLabel?: string;
    /** Back to editing the brochure being read. */
    onEdit?: () => void;
  } | null>(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTemplates(await templatesApi.list());
    } catch (e) {
      message.error("Could not load templates.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Checking happens the moment a file is chosen, so the report is on screen
  // before anyone decides whether to keep it.
  const onFile = async (chosen: File) => {
    setFile(chosen);
    setChecked(null);
    setChecking(true);
    try {
      setChecked(await templatesApi.check(chosen));
      form.setFieldsValue({ name: chosen.name.replace(/\.pptx$/i, "") });
    } catch (e: any) {
      message.error(e?.response?.data?.detail ?? "Could not read that file.");
      setFile(null);
    } finally {
      setChecking(false);
    }
  };

  /**
   * The copy keys this file draws that nobody has described yet, and the ones
   * already on record. Read off the check result rather than tracked
   * separately, so re-checking is the only thing that has to stay correct.
   */
  const undeclaredCopyProblems = (checked?.validation.problems ?? [])
    .filter((problem) => problem.kind === "undeclared_copy");
  const undeclaredCopy = undeclaredCopyProblems.map((problem) => problem.name);

  const reusedCopy = (checked?.validation.copy_fields ?? [])
    .filter((key) => !undeclaredCopy.includes(key));

  /** Re-read the file after describing its fields, so the report agrees. */
  const recheck = async () => {
    if (!file) return;
    setChecking(true);
    try {
      setChecked(await templatesApi.check(file));
    } catch (e: any) {
      message.error("Described them, but could not re-check the file.");
    } finally {
      setChecking(false);
    }
  };

  const save = async () => {
    if (!file) return;
    const values = await form.validateFields();
    setSaving(true);
    try {
      // `scope` is the question asked; the API stores the answer as whichever of
      // the two targeting fields it implies, and rejects both being set.
      const { scope, destination, listings, ...rest } = values;
      await templatesApi.upload(file, {
        ...rest,
        destination: scope === "destination" ? destination : null,
        listings: scope === "listing" ? listings : [],
        force: !checked?.validation.valid,
      });
      message.success("Template saved.");
      closeUpload();
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail ?? "Could not save the template.");
    } finally {
      setSaving(false);
    }
  };

  const closeUpload = () => {
    setUploadOpen(false);
    setFile(null);
    setChecked(null);
    setUploadScope("default");
    form.resetFields();
  };

  const revalidate = async (template: IPresentationTemplate) => {
    try {
      const { validation } = await templatesApi.revalidate(Number(template.id));
      message[validation.valid ? "success" : "warning"](
        validation.valid
          ? "Template checks out."
          : `${validation.problems.length} problem(s) found.`
      );
      load();
    } catch (e) {
      message.error("Could not re-check that template.");
    }
  };

  const toggleActive = async (template: IPresentationTemplate) => {
    try {
      await templatesApi.update(Number(template.id), { is_active: !template.is_active });
      load();
    } catch (e) {
      message.error("Could not update the template.");
    }
  };

  const viewPdf = (title: string, url: string, downloadUrl?: string | null,
                   onEdit?: () => void) =>
    setViewing({ title, url, loading: false, downloadUrl, onEdit });

  const previewTemplate = async (template: IPresentationTemplate) => {
    // The first preview of a deck takes about fifteen seconds, nearly all of it
    // moving 35 MB to and from S3, so the modal opens immediately and says so
    // rather than leaving a button looking broken.
    setViewing({
      title: template.name,
      url: template.preview_url,
      loading: !template.preview_url,
      hint: "Rendering the template — about fifteen seconds the first time.",
      downloadUrl: template.file_url,
      downloadLabel: "Download .pptx",
    });
    if (template.preview_url) return;

    try {
      const url = await templatesApi.preview(Number(template.id));
      setViewing((current) =>
        current && current.title === template.name
          ? { ...current, url, loading: false }
          : current
      );
      load();
    } catch (e: any) {
      setViewing((current) =>
        current && current.title === template.name
          ? {
              ...current,
              loading: false,
              error:
                e?.response?.data?.detail ??
                "Could not render a preview of this template.",
            }
          : current
      );
    }
  };

  // The monitor lists presentations, the drawer lists a template's properties,
  // so "manage this one" is the drawer opened at that property's row.
  const manage = (templateId: number, listingName: string) => {
    const template = templates.find((t) => Number(t.id) === templateId);
    if (!template) {
      message.error("That template is not in the list any more.");
      return;
    }
    setDrawerQuery(listingName);
    setOpened(template);
  };

  const shown = showInactive ? templates : templates.filter((t) => t.is_active);
  const inactiveCount = templates.length - templates.filter((t) => t.is_active).length;

  return (
    <Container className="anti-contento contento">
      <PageHeader
        title="Templates"
        subTitle="PowerPoint designs the system fills with live property data"
        extra={[
          <Button key="upload" type="primary" onClick={() => setUploadOpen(true)}>
            Upload template
          </Button>,
        ]}
      />

      <Tabs
        defaultActiveKey="templates"
        destroyInactiveTabPane
        tabBarExtraContent={
          inactiveCount > 0 && (
            <span className="tpl-meta">
              <Switch
                size="small"
                checked={showInactive}
                onChange={setShowInactive}
                style={{ marginRight: 8 }}
              />
              Show inactive ({inactiveCount})
            </span>
          )
        }
      >
        <Tabs.TabPane tab="Templates" key="templates">
      {loading ? (
        <div style={{ padding: 60, textAlign: "center" }}>
          <Spin />
        </div>
      ) : templates.length === 0 ? (
        <Empty description="No templates yet — upload a .pptx to get started" />
      ) : shown.length === 0 ? (
        <Empty description="No active templates — switch on “Show inactive” to see the rest" />
      ) : (
        <div className="gallery">
          {shown.map((template) => (
            <Card
                key={template.id}
                size="small"
                hoverable
                onClick={() => setOpened(template)}
                // Full height of its grid cell, so cards on the same row line
                // up however much description each one carries.
                style={{ height: "100%", opacity: template.is_active ? 1 : 0.6 }}
                cover={
                  // A design is recognised by sight, so the real slide goes here
                  // when there is one. `cover` with a top anchor keeps the grid
                  // even across portrait and landscape decks while still showing
                  // the part of the slide that carries the branding.
                  <div className="thumb">
                    {template.thumbnail_url ? (
                      <img src={template.thumbnail_url} alt="" loading="lazy" />
                    ) : (
                      <FileTextOutlined />
                    )}
                  </div>
                }
                actions={[
                  <Tooltip key="edit" title="Edit name, targeting and status">
                    <EditOutlined
                      onClick={(event) => {
                        event.stopPropagation();
                        setEditing(template);
                      }}
                    />
                  </Tooltip>,
                  <Tooltip key="preview" title="Look through the template">
                    <EyeOutlined
                      onClick={(event) => {
                        event.stopPropagation();
                        previewTemplate(template);
                      }}
                    />
                  </Tooltip>,
                  <ReloadOutlined
                    key="check"
                    onClick={(event) => {
                      event.stopPropagation();
                      revalidate(template);
                    }}
                  />,
                  // Only on templates that actually need it, so the row of
                  // actions does not grow an icon nobody can act on. A deck
                  // uploaded before its fields were described lands here — the
                  // fix must not be "upload it again".
                  ...((template.undeclared_copy_keys?.length ?? 0) > 0
                    ? [(
                      <Tooltip
                        key="copy"
                        title={`${template.undeclared_copy_keys.length} copy `
                          + "list(s) need describing before this can generate"}
                      >
                        <span onClick={(event) => event.stopPropagation()}>
                          <DeclareCopyFieldsModal
                            keys={template.undeclared_copy_keys}
                            shapes={template.copy_lists}
                            slidesByKey={template.manifest?.token_slides}
                            reusedKeys={(template.copy_field_keys ?? []).filter(
                              (key) => !template.undeclared_copy_keys.includes(key))}
                            onDeclared={() => revalidate(template)}
                          >
                            <FormOutlined style={{ color: "#fa8c16" }} />
                          </DeclareCopyFieldsModal>
                        </span>
                      </Tooltip>
                    )]
                    : []),
                ]}
              >
                <Card.Meta
                  title={template.name}
                  description={
                    <>
                      <Tag
                        className="scope-tag"
                        color={SCOPE_COLORS[template.scope]}
                      >
                        {template.scope === "destination" && template.destination_name
                          ? `Destination — ${template.destination_name}`
                          : SCOPE_COUNT_LABELS[template.scope](
                              template.listings?.length ?? 0
                            )}
                      </Tag>
                      <Tag className="scope-tag">
                        {template.language.toUpperCase()}
                      </Tag>
                      {!template.is_active && <Tag className="scope-tag">Inactive</Tag>}
                      <div className="tpl-meta" style={{ marginTop: 6 }}>
                        {NAMESPACE_LABELS[template.namespace] ?? template.namespace}
                        {" · "}
                        {template.slide_count} slides
                        {template.hidden_slide_count > 0 && (
                          <> ({template.hidden_slide_count} hidden)</>
                        )}
                        {" · "}
                        {template.token_count} tokens
                      </div>
                      {/* The count is the whole reason the separate coverage page
                          is gone: it belongs to the design it was made from. */}
                      <div className="tpl-count">
                        {template.presentation_count === 0
                          ? "Nothing generated yet"
                          : `${template.presentation_count} presentation${
                              template.presentation_count === 1 ? "" : "s"
                            }`}
                        {template.attention_count > 0 && (
                          <Badge
                            status="warning"
                            text={`${template.attention_count} need attention`}
                            style={{ marginLeft: 8 }}
                          />
                        )}
                      </div>
                    </>
                  }
                />
            </Card>
          ))}
        </div>
      )}
        </Tabs.TabPane>
        <Tabs.TabPane tab="Monitor" key="monitor">
          <PresentationMonitor onViewPdf={viewPdf} onManage={manage} />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Drive folders" key="drive">
          <DriveFolders />
        </Tabs.TabPane>
      </Tabs>

      <TemplateDrawer
        template={opened}
        initialQuery={drawerQuery}
        onClose={() => { setOpened(null); setDrawerQuery(undefined); }}
        onChanged={load}
        onPreview={previewTemplate}
        onViewPdf={viewPdf}
        onEdit={setEditing}
      />

      {editing && (
        <TemplateEditModal
          template={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            load();
            // The drawer is showing the template that just changed.
            if (opened && opened.id === editing.id) setOpened(null);
          }}
        />
      )}

      {viewing && (
        <PdfPreviewModal
          title={viewing.title}
          url={viewing.url}
          loading={viewing.loading}
          loadingHint={viewing.hint}
          error={viewing.error}
          downloadUrl={viewing.downloadUrl}
          downloadLabel={viewing.downloadLabel}
          onEdit={viewing.onEdit}
          onClose={() => setViewing(null)}
        />
      )}

      <Modal
        visible={uploadOpen}
        onCancel={closeUpload}
        title="Upload template"
        width={720}
        okText="Save template"
        okButtonProps={{ disabled: !file || checking, loading: saving }}
        onOk={save}
      >
        {!file ? (
          <Upload.Dragger
            accept=".pptx"
            maxCount={1}
            beforeUpload={(chosen) => {
              onFile(chosen as unknown as File);
              return false;
            }}
            showUploadList={false}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Drop a .pptx here, or click to choose</p>
            <p className="ant-upload-hint">
              It is checked against the property data before anything is saved.
            </p>
          </Upload.Dragger>
        ) : (
          <>
            <p>
              <b>{file.name}</b>{" "}
              <Button type="link" size="small" onClick={() => setFile(null)}>
                choose another
              </Button>
            </p>

            {checking ? (
              <div style={{ padding: 32, textAlign: "center" }}>
                <Spin tip="Reading the template…" />
              </div>
            ) : (
              checked && (
                <ValidationReport result={checked}>
                  {/*
                    Offered before the template is stored, while the person still
                    has the file in front of them — the same principle the rest
                    of the upload check follows. Describing the fields re-runs
                    the check, so the report turns green and the deck saves
                    without `force`.
                  */}
                  {undeclaredCopy.length > 0 && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginTop: 12 }}
                      message={`${undeclaredCopy.length} copy list${
                        undeclaredCopy.length === 1 ? "" : "s"} need describing`}
                      description={
                        <>
                          This template asks for text a person writes per
                          property. Say what each list is for and it becomes
                          fillable.
                          <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                            {undeclaredCopyProblems.map((problem) => (
                              <li key={problem.name}>
                                <code>{`{{${problem.name}}}`}</code>
                                {problem.slides.length > 0 && (
                                  <> on slide {problem.slides.join(", ")}</>
                                )}
                              </li>
                            ))}
                          </ul>
                          <div style={{ marginTop: 8 }}>
                            <DeclareCopyFieldsModal
                              keys={undeclaredCopy}
                              shapes={checked.validation.copy_lists}
                              slidesByKey={checked.manifest.token_slides}
                              reusedKeys={reusedCopy}
                              onDeclared={recheck}
                            >
                              <Button type="primary" size="small">
                                Describe them
                              </Button>
                            </DeclareCopyFieldsModal>
                          </div>
                        </>
                      }
                    />
                  )}
                </ValidationReport>
              )
            )}

            <Form
              form={form}
              layout="vertical"
              style={{ marginTop: 20 }}
              initialValues={{ scope: "default", listings: [] }}
              onValuesChange={(changed) => {
                if (changed.scope) setUploadScope(changed.scope);
              }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="name"
                    label="Name"
                    rules={[{ required: true, message: "Give it a name" }]}
                  >
                    <Input placeholder="Madrid Brochure" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="namespace" label="Type" initialValue="brochure">
                    <Select
                      options={Object.entries(NAMESPACE_LABELS).map(
                        ([value, label]) => ({ value, label })
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="language" label="Language" initialValue="es">
                    <Select
                      options={[
                        { value: "es", label: "Spanish" },
                        { value: "en", label: "English" },
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <TemplateScopeFields scope={uploadScope} />
            </Form>

            {checked && !checked.validation.valid && (
              <Alert
                type="warning"
                showIcon
                message="Saving anyway will store a template that cannot be generated until it is fixed."
              />
            )}
          </>
        )}
      </Modal>
    </Container>
  );
}

export default Templates;
